/**
 * The spread editor: current bets, metadata, and an undo/redo history.
 *
 * CLAUDE.md pins the editor state to `solid-js/store` so the felt and the wheel
 * re-render only the pockets that actually changed. Every bet mutation funnels
 * through one `commit()`, which is the only place history is touched.
 */

import { batch, createSignal } from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import type { PlacedBet, Spread, WheelStyle } from '~/domain/types';
import {
  addBet,
  createSpread,
  newSpreadId,
  normaliseBets,
  removeBet,
  removeChips,
} from '~/domain/spread';
import { betLabel } from '~/domain/bets';

/** SPEC.md §4 asks for at least 50 steps. */
export const MAX_HISTORY = 50;

interface HistoryEntry {
  readonly bets: PlacedBet[];
  readonly label: string | null;
}

export interface SpreadEditor {
  bets: () => PlacedBet[];
  wheelStyle: () => WheelStyle;
  name: () => string;
  tags: () => string[];
  spreadId: () => string;
  dirty: () => boolean;
  canUndo: () => boolean;
  canRedo: () => boolean;
  undoDepth: () => number;
  redoDepth: () => number;
  lastAction: () => string | null;

  placeBet: (bet: PlacedBet) => void;
  removeChipsFrom: (target: Pick<PlacedBet, 'betType' | 'target'>, chips: number) => void;
  clearBet: (target: Pick<PlacedBet, 'betType' | 'target'>) => void;
  applyBets: (bets: readonly PlacedBet[], label: string) => void;
  replaceBets: (bets: readonly PlacedBet[], label: string) => void;
  scaleChips: (factor: number) => void;
  clearAll: () => void;

  setName: (name: string) => void;
  setTags: (tags: string[]) => void;

  undo: () => void;
  redo: () => void;

  loadSpread: (spread: Spread) => void;
  newSpread: (style: WheelStyle) => void;
  toSpread: () => Spread;
  markSaved: () => void;
}

/** Content signature — drives both the no-op guard and the dirty flag. */
function signatureOf(bets: readonly PlacedBet[], name: string, tags: readonly string[]): string {
  const parts = bets.map((bet) => `${bet.betType}:${bet.target}:${bet.chips}`);
  return `${name} ${[...tags].sort().join('|')} ${parts.join(',')}`;
}

export function createSpreadEditor(initial?: { style?: WheelStyle }): SpreadEditor {
  const seed = createSpread('Untitled spread', initial?.style ?? 'single-zero');

  const [state, setState] = createStore<{ bets: PlacedBet[] }>({ bets: [] });
  const [wheelStyle, setWheelStyle] = createSignal<WheelStyle>(seed.wheelStyle);
  const [name, setNameSignal] = createSignal(seed.name);
  const [tags, setTagsSignal] = createSignal<string[]>([]);
  const [spreadId, setSpreadId] = createSignal(seed.id);
  const [createdAt, setCreatedAt] = createSignal(seed.createdAt);

  const [past, setPast] = createSignal<HistoryEntry[]>([]);
  const [future, setFuture] = createSignal<HistoryEntry[]>([]);
  const [lastAction, setLastAction] = createSignal<string | null>(null);
  const [savedSignature, setSavedSignature] = createSignal(
    signatureOf([], seed.name, []),
  );

  const currentSignature = (): string => signatureOf(state.bets, name(), tags());

  /** Replace the bet list without touching history. */
  const applyBetList = (bets: PlacedBet[]) => {
    setState('bets', reconcile(bets, { key: null }));
  };

  /**
   * The single place a bet mutation lands. A commit that changes nothing is
   * dropped, so a stray click never fills the undo stack with no-ops.
   */
  const commit = (next: readonly PlacedBet[], label: string) => {
    const normalised = normaliseBets(next);
    if (signatureOf(normalised, name(), tags()) === currentSignature()) return;

    const entry: HistoryEntry = { bets: state.bets.map((bet) => ({ ...bet })), label: lastAction() };
    batch(() => {
      const trimmed = past().length >= MAX_HISTORY ? past().slice(1) : past();
      setPast([...trimmed, entry]);
      setFuture([]);
      applyBetList(normalised);
      setLastAction(label);
    });
  };

  const restore = (entry: HistoryEntry, opposite: 'past' | 'future') => {
    const current: HistoryEntry = {
      bets: state.bets.map((bet) => ({ ...bet })),
      label: lastAction(),
    };
    batch(() => {
      if (opposite === 'future') setFuture([...future(), current]);
      else setPast([...past(), current]);
      applyBetList(entry.bets);
      setLastAction(entry.label);
    });
  };

  const resetTo = (spread: Spread) => {
    batch(() => {
      setSpreadId(spread.id);
      setWheelStyle(spread.wheelStyle);
      setNameSignal(spread.name);
      setTagsSignal([...spread.tags]);
      setCreatedAt(spread.createdAt);
      applyBetList(normaliseBets(spread.bets));
      setPast([]);
      setFuture([]);
      setLastAction(null);
      setSavedSignature(signatureOf(normaliseBets(spread.bets), spread.name, spread.tags));
    });
  };

  return {
    bets: () => state.bets,
    wheelStyle,
    name,
    tags,
    spreadId,
    lastAction,
    dirty: () => currentSignature() !== savedSignature(),
    canUndo: () => past().length > 0,
    canRedo: () => future().length > 0,
    undoDepth: () => past().length,
    redoDepth: () => future().length,

    placeBet: (bet) => commit(addBet(state.bets, bet), `Place ${betLabel(bet)}`),

    removeChipsFrom: (target, chips) =>
      commit(removeChips(state.bets, target, chips), `Remove ${chips} from ${betLabel({ ...target, chips })}`),

    clearBet: (target) =>
      commit(removeBet(state.bets, target), `Clear ${betLabel({ ...target, chips: 0 })}`),

    applyBets: (bets, label) => commit([...state.bets, ...bets], label),

    replaceBets: (bets, label) => commit(bets, label),

    /**
     * Scale every stake. A bet that would round below one chip is clamped to
     * one rather than dropped — scaling a spread must not silently change which
     * pockets it covers.
     */
    scaleChips: (factor) =>
      commit(
        state.bets.map((bet) => ({ ...bet, chips: Math.max(1, Math.round(bet.chips * factor)) })),
        `Scale chips ×${factor}`,
      ),

    clearAll: () => commit([], 'Clear all bets'),

    setName: (next) => setNameSignal(next),
    setTags: (next) => setTagsSignal([...next]),

    undo: () => {
      const entries = past();
      const entry = entries[entries.length - 1];
      if (!entry) return;
      setPast(entries.slice(0, -1));
      restore(entry, 'future');
    },

    redo: () => {
      const entries = future();
      const entry = entries[entries.length - 1];
      if (!entry) return;
      setFuture(entries.slice(0, -1));
      restore(entry, 'past');
    },

    loadSpread: (spread) => resetTo(spread),

    newSpread: (style) => resetTo(createSpread('Untitled spread', style)),

    toSpread: () => ({
      id: spreadId(),
      name: name(),
      wheelStyle: wheelStyle(),
      bets: state.bets.map((bet) => ({ ...bet })),
      tags: [...tags()],
      createdAt: createdAt(),
      updatedAt: Date.now(),
    }),

    markSaved: () => setSavedSignature(currentSignature()),
  };
}

/** A fresh id, for duplicating a spread in the library. */
export { newSpreadId };
